import { LoadingOutlined } from "@ant-design/icons";
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { Button, Drawer, Popover, Select, message } from "antd";
import Utils from "@utils/utils";
import { BATCH_IMAGES_TYPE, GENERATE_MODE, IMAGE_TYPE } from "@utils/CONST";
import Iconfont from "@utils/Iconfont";
import { AppContext } from "@utils/AppContext";
import { GenerateContext } from "@view/page_v3/hooks/GenerateContext";
import { workflowOfGenerate } from "@utils/workflowOfBuildTaskV2";
import SelectTryonPose from "@view/page_v3/BatchProductImages/components/SelectTryonPose";
import eventBus from "@utils/eventBus";
import styles from "./index.module.scss";

const SIZE_OPTIONS = [
    { value: GENERATE_MODE.STD, label: "2K" },
    { value: GENERATE_MODE.HQ, label: "4K" },
];

const COUNT_OPTIONS = [
    { value: 1, label: "1张" },
    { value: 2, label: "2张" },
    { value: 4, label: "4张" },
    { value: 8, label: "8张" },
];

const MAX_UPLOAD_COUNT = 14;
const REFERENCE_DRAWER_TABS = ["custom", "enterpriseCustom"];
const ACCEPT_IMAGE_MIME_TYPES = ["image/jpeg", "image/png", "image/webp", "image/avif"];
const UNSUPPORTED_FILE_MESSAGE = "您只能上传JPG/PNG/WEBP/AVIF格式的文件";

const getUploadErrorMessage = (reasonList = []) => {
    const uniqueReasons = [...new Set((reasonList || []).filter(Boolean))];
    if (!uniqueReasons.length) return "图片不符合要求";
    return `${uniqueReasons.join("，")}`;
};

const UploadInput = ({ createClick }) => {
    const { setSelectedTaskListId, globalUser } = useContext(AppContext);
    const generateContext = useContext(GenerateContext);
    const {
        projectId,
        setProjectId,
        prompt,
        setPrompt,
        generateNum,
        setGenerateNum,
        imageSize,
        setImageSize,
        matchImages,
        setMatchImages,
    } = generateContext;

    const [errorList, setErrorList] = useState([]);
    const [drawOpen, setDrawOpen] = useState(false);
    const [dragging, setDragging] = useState(false);
    const [uploadMenuOpen, setUploadMenuOpen] = useState(false);
    const [uploadingCount, setUploadingCount] = useState(0);
    const [pendingUploadList, setPendingUploadList] = useState([]);
    const [generateLoading, setGenerateLoading] = useState(false);
    const [showPlaceholder, setShowPlaceholder] = useState(!prompt);

    const uploadAbort = useRef(false);
    const dragCounterRef = useRef(0);
    const materialRef = useRef(null);
    const localUploadInputRef = useRef(null);
    const contentEditableRef = useRef(null);
    const isComposing = useRef(false);
    const matchImagesRef = useRef(matchImages);
    const pendingUploadListRef = useRef([]);
    const pendingUploadCountRef = useRef(0);
    const uploadQueueRef = useRef([]);
    const uploadBatchKeySetRef = useRef(new Set());
    const isUploadProcessingRef = useRef(false);
    const pendingUploadTokenRef = useRef(0);
    const lastTriggerRef = useRef({
        batchKey: "",
        source: "",
        timestamp: 0,
    });

    useEffect(() => {
        matchImagesRef.current = matchImages;
    }, [matchImages]);

    useEffect(() => {
        pendingUploadListRef.current = pendingUploadList;
        pendingUploadCountRef.current = pendingUploadList.length;
    }, [pendingUploadList]);

    useEffect(() => {
        return () => {
            pendingUploadListRef.current.forEach((item) => {
                if (item?.previewUrl) {
                    URL.revokeObjectURL(item.previewUrl);
                }
            });
        };
    }, []);

    const KEYWORDS = useMemo(() => {
        const kws = Array.from({ length: MAX_UPLOAD_COUNT }, (_, i) => `图${i + 1}`);
        return kws.sort((a, b) => b.length - a.length);
    }, []);

    const getUploadBatchKey = (files) => {
        return Array.from(files || [])
            .map((f) => `${f?.uid || ""}-${f?.name || ""}-${f?.size || ""}-${f?.lastModified || ""}`)
            .join("|");
    };

    const getPendingItemBaseId = (file) => {
        return `${file?.uid || ""}-${file?.name || ""}-${file?.size || ""}-${file?.lastModified || ""}`;
    };

    const createPendingUploadToken = (file) => {
        pendingUploadTokenRef.current += 1;
        return `${getPendingItemBaseId(file)}-${pendingUploadTokenRef.current}`;
    };

    const removePendingUploadItem = (pendingId) => {
        if (!pendingId) return;

        setPendingUploadList((prev) => {
            const target = prev.find((item) => item.id === pendingId);
            if (target?.previewUrl) {
                URL.revokeObjectURL(target.previewUrl);
            }
            const nextList = prev.filter((item) => item.id !== pendingId);
            pendingUploadListRef.current = nextList;
            pendingUploadCountRef.current = nextList.length;
            return nextList;
        });
    };

    const markPendingUploadItemStatus = (pendingId, status) => {
        if (!pendingId) return;

        setPendingUploadList((prev) => {
            const nextList = prev.map((item) => (
                item.id === pendingId ? { ...item, status } : item
            ));
            pendingUploadListRef.current = nextList;
            pendingUploadCountRef.current = nextList.length;
            return nextList;
        });
    };

    const triggerUploadBatch = (files, initialErrorReasons = [], source = "unknown") => {
        const nextFiles = Array.from(files || []);
        if (!nextFiles.length) return false;

        const remainCount = Math.max(
            0,
            MAX_UPLOAD_COUNT - matchImagesRef.current.length - pendingUploadCountRef.current
        );
        const acceptedFiles = nextFiles.slice(0, remainCount);
        if (!acceptedFiles.length) {
            message.warning(`最多可上传${MAX_UPLOAD_COUNT}张图`);
            return false;
        }

        const batchKey = getUploadBatchKey(acceptedFiles);
        const now = Date.now();
        const isDuplicateBurst =
            lastTriggerRef.current.batchKey === batchKey
            && lastTriggerRef.current.source === source
            && now - lastTriggerRef.current.timestamp < 400;
        if (isDuplicateBurst) return false;

        lastTriggerRef.current = {
            batchKey,
            source,
            timestamp: now,
        };
        if (uploadBatchKeySetRef.current.has(batchKey)) return false;

        const pendingItems = acceptedFiles.map((file) => ({
            id: createPendingUploadToken(file),
            name: file?.name || "待上传图片",
            status: "queued",
            fileKey: getPendingItemBaseId(file),
            previewUrl: URL.createObjectURL(file),
        }));

        const queueItems = acceptedFiles.map((file, index) => ({
            file,
            pendingId: pendingItems[index]?.id || "",
        }));

        setPendingUploadList((prev) => {
            const nextList = [...prev, ...pendingItems];
            pendingUploadListRef.current = nextList;
            pendingUploadCountRef.current = nextList.length;
            return nextList;
        });
        uploadBatchKeySetRef.current.add(batchKey);
        uploadQueueRef.current.push({ queueItems, initialErrorReasons, batchKey });
        processUploadQueue();
        return true;
    };

    const processUploadQueue = async () => {
        if (isUploadProcessingRef.current) return;

        isUploadProcessingRef.current = true;
        while (uploadQueueRef.current.length > 0) {
            const currentBatch = uploadQueueRef.current.shift();
            await handleUpload(currentBatch.queueItems, currentBatch.initialErrorReasons, currentBatch.batchKey);
        }
        isUploadProcessingRef.current = false;
    };

    const isFileDragEvent = (e) => {
        const types = e?.dataTransfer?.types;
        return Array.isArray(types) ? types.includes("Files") : types?.contains?.("Files");
    };

    const resetDragState = () => {
        dragCounterRef.current = 0;
        setDragging(false);
    };

    const handleDragEnter = (e) => {
        if (!isFileDragEvent(e)) return;

        e.preventDefault();
        e.stopPropagation();
        dragCounterRef.current += 1;
        setDragging(true);
    };

    const handleDragOver = (e) => {
        if (!isFileDragEvent(e)) return;

        e.preventDefault();
        e.stopPropagation();
        if (!dragging) {
            setDragging(true);
        }
    };

    const handleDragLeave = (e) => {
        if (!isFileDragEvent(e)) return;

        e.preventDefault();
        e.stopPropagation();
        dragCounterRef.current = Math.max(0, dragCounterRef.current - 1);
        if (dragCounterRef.current === 0) {
            setDragging(false);
        }
    };

    const handleDrop = (e) => {
        if (!isFileDragEvent(e)) return;

        e.preventDefault();
        e.stopPropagation();
        resetDragState();

        const droppedFiles = Array.from(e.dataTransfer?.files || []);
        const files = droppedFiles.filter((file) => ACCEPT_IMAGE_MIME_TYPES.includes((file?.type || "").toLowerCase()));
        const initialErrorReasons = droppedFiles.length > files.length ? [UNSUPPORTED_FILE_MESSAGE] : [];
        if (!files.length) {
            if (initialErrorReasons.length > 0) {
                message.error(getUploadErrorMessage(initialErrorReasons), 5);
            }
            return;
        }

        triggerUploadBatch(files, initialErrorReasons, "drop");
    };

    const handleUpload = async (queueItems = [], initialErrorReasons = [], batchKey = "") => {
        if (queueItems.length > MAX_UPLOAD_COUNT) {
            message.warning(`最多可上传${MAX_UPLOAD_COUNT}张图`);
        }

        const nextErrorList = [];
        const errorReasonList = [...initialErrorReasons];
        setErrorList([]);
        setUploadingCount((count) => count + 1);
        const pushUploadError = (file, reason) => {
            nextErrorList.push({
                name: file?.name || "未命名图片",
                reason: reason || "图片不符合要求",
            });
            if (reason) errorReasonList.push(reason);
            setErrorList([...nextErrorList]);
        };

        try {
            const remainCount = Math.max(0, MAX_UPLOAD_COUNT - matchImagesRef.current.length);
            const nextQueueItems = [...queueItems].slice(0, remainCount);

            for (const queueItem of nextQueueItems) {
                let file = queueItem?.file;
                const pendingId = queueItem?.pendingId || "";
                if (uploadAbort.current) {
                    removePendingUploadItem(pendingId);
                    uploadAbort.current = false;
                    break;
                }

                markPendingUploadItemStatus(pendingId, "uploading");

                try {
                    await Utils.validateImageFile(file, true);
                } catch (err) {
                    removePendingUploadItem(pendingId);
                    pushUploadError(file, err?.message);
                    continue;
                }

                if (uploadAbort.current) {
                    removePendingUploadItem(pendingId);
                    uploadAbort.current = false;
                    break;
                }

                const resizeRes = await Utils.resizeImageFileMaxByAliyunBy(file, 4096);
                if (!resizeRes || !resizeRes.success) {
                    removePendingUploadItem(pendingId);
                    pushUploadError(file, resizeRes?.message || "图片处理失败，请更换图片后重试");
                    continue;
                }
                file = resizeRes.file;

                if (uploadAbort.current) {
                    removePendingUploadItem(pendingId);
                    uploadAbort.current = false;
                    break;
                }

                const res = await Utils.checkImageIslegal(file, 0, IMAGE_TYPE.CREATIVE_IMAGE_GEN);
                if (!res || !res.isLegal) {
                    removePendingUploadItem(pendingId);
                    pushUploadError(file, "图片内容不符合要求，请更换后重试");
                    continue;
                }

                const nextImage = { url: res.data };
                removePendingUploadItem(pendingId);
                setMatchImages((prev) => {
                    const nextList = [...prev, nextImage].slice(0, MAX_UPLOAD_COUNT);
                    matchImagesRef.current = nextList;
                    return nextList;
                });
            }
        } catch (err) {
            message.warning(err.message);
        } finally {
            setErrorList([...nextErrorList]);
            if (nextErrorList.length > 0 || errorReasonList.length > 0) {
                const reasonList = [
                    ...errorReasonList,
                    ...nextErrorList.map((item) => item?.reason),
                ];
                message.error(getUploadErrorMessage(reasonList), 5);
            }
            if (batchKey) {
                uploadBatchKeySetRef.current.delete(batchKey);
            }
            setUploadingCount((count) => Math.max(0, count - 1));
        }
    };

    const handleDelete = (index) => {
        const nextSuccessList = [...matchImages];
        nextSuccessList.splice(index, 1);
        matchImagesRef.current = nextSuccessList;
        setMatchImages(nextSuccessList);
    };

    const handlePromptChange = (e) => {
        let nextValue = e.target.value || "";
        if (nextValue.length > 300) {
            nextValue = nextValue.slice(0, 300);
            message.warning("最多可输入300字");
        }
        setPrompt(nextValue);
    };

    const handleOpenDrawer = () => {
        setUploadMenuOpen(false);
        setDrawOpen(true);
    };

    const saveSelection = (containerEl) => {
        const selection = window.getSelection();
        if (!selection || selection.rangeCount === 0) return null;

        const range = selection.getRangeAt(0);
        const preSelectionRange = range.cloneRange();
        preSelectionRange.selectNodeContents(containerEl);
        preSelectionRange.setEnd(range.startContainer, range.startOffset);
        const start = preSelectionRange.toString().length;

        return {
            start,
            end: start + range.toString().length,
        };
    };

    const restoreSelection = (containerEl, savedSelection) => {
        if (!savedSelection) return;

        let charIndex = 0;
        const range = document.createRange();
        range.setStart(containerEl, 0);
        range.collapse(true);
        const nodeStack = [containerEl];
        let node;
        let foundStart = false;
        let stop = false;

        while (!stop && (node = nodeStack.pop())) {
            if (node.nodeType === 3) {
                const nextCharIndex = charIndex + node.length;
                if (!foundStart && savedSelection.start >= charIndex && savedSelection.start <= nextCharIndex) {
                    range.setStart(node, savedSelection.start - charIndex);
                    foundStart = true;
                }
                if (foundStart && savedSelection.end >= charIndex && savedSelection.end <= nextCharIndex) {
                    range.setEnd(node, savedSelection.end - charIndex);
                    stop = true;
                }
                charIndex = nextCharIndex;
            } else {
                let i = node.childNodes.length;
                while (i--) {
                    nodeStack.push(node.childNodes[i]);
                }
            }
        }

        const selection = window.getSelection();
        if (!selection) return;
        selection.removeAllRanges();
        selection.addRange(range);
    };

    const formatText = useCallback((text) => {
        const pattern = new RegExp(`(${KEYWORDS.join("|")})`, "g");
        return (text || "").replace(pattern, '<span class="keyword-highlight">$1</span>');
    }, [KEYWORDS]);

    const handleContentInput = useCallback((e) => {
        let text = e.target.innerText || "";

        if (isComposing.current) return;

        if (text.length > 300) {
            text = text.slice(0, 300);
            message.warning("最多可输入300字");
        }

        const isEmpty = text.replace(/\n/g, "").length === 0;
        if (isEmpty) {
            if (e.target.innerHTML !== "") e.target.innerHTML = "";
            setShowPlaceholder(true);
            setPrompt("");
            return;
        }

        const selection = saveSelection(e.target);
        const newHTML = formatText(text);
        if (e.target.innerHTML !== newHTML) {
            e.target.innerHTML = newHTML;
            restoreSelection(e.target, selection);
        }
        setShowPlaceholder(false);
        setPrompt(text);
    }, [formatText, setPrompt]);

    const handleCompositionStart = () => {
        isComposing.current = true;
        setShowPlaceholder(false);
    };

    const handleCompositionEnd = (e) => {
        isComposing.current = false;
        handleContentInput(e);
    };

    useEffect(() => {
        if (!contentEditableRef.current) return;
        const formattedPrompt = formatText(prompt || "");
        if (contentEditableRef.current.innerHTML !== formattedPrompt) {
            contentEditableRef.current.innerHTML = formattedPrompt;
        }
        setShowPlaceholder(!(prompt || "").trim());
    }, [formatText, prompt]);

    const handleDrawerClose = () => {
        setDrawOpen(false);
    };

    const handleReferenceChange = (value) => {
        const nextImages = Array.isArray(value) ? value.slice(0, MAX_UPLOAD_COUNT) : [];
        matchImagesRef.current = nextImages;
        setMatchImages(nextImages);
        handleDrawerClose();
    };

    const handleUploadMenuOpenChange = (open) => {
        setUploadMenuOpen(open);
    };

    const handleLocalUploadClick = () => {
        const input = localUploadInputRef.current;
        if (!input) return;

        setUploadMenuOpen(false);
        input.value = "";
        input.click();
    };

    const handleLocalFileChange = (e) => {
        const files = Array.from(e.target.files || []);
        if (!files.length) return;

        triggerUploadBatch(files, [], "select");
        e.target.value = "";
    };

    const handleGenerate = async () => {
        const nextPrompt = (prompt || "").trim();
        try {
            const status = Utils.checkLoginStatus(() => handleGenerate());
            if (!status) return;

            const isPaidUser = Number(globalUser?.membership?.level) !== 0;
            if (!isPaidUser) {
                message.warning("该功能为会员用户专属,请充值会员后使用");
                eventBus.emit("recharge");
                return;
            }

            setGenerateLoading(true);
            setPrompt(nextPrompt);

            const nextProjectId = await workflowOfGenerate(IMAGE_TYPE.CREATIVE_IMAGE_GEN, {
                ...generateContext,
                prompt: nextPrompt,
                generateNum,
                imageSize,
                matchImages,
            }, {
                projectId,
            });

            if (!nextProjectId) return;

            setSelectedTaskListId(nextProjectId);
            setProjectId(nextProjectId);
            message.success("生成中，请稍后");
            createClick?.();
        } catch (err) {
            message.warning(err.message);
        } finally {
            setGenerateLoading(false);
        }
    };

    const uploadMenuContent = (
        <div className={styles.UploadMenu}>
            <div className={styles.UploadMenuItem} onClick={handleLocalUploadClick}>
                本地上传
            </div>
            <div className={styles.UploadMenuItem} onClick={handleOpenDrawer}>
                从素材库选择
            </div>
        </div>
    );

    const isUploading = uploadingCount > 0;
    const costPerImage = imageSize === GENERATE_MODE.HQ ? 120 : 80;
    const totalCost = costPerImage * generateNum;
    const canGenerate = !isUploading && (matchImages.length > 0 || prompt.trim().length > 0);
    const generateButton = (
        <Button
            type="primary"
            className={styles.GenerateBtn}
            disabled={!canGenerate}
            loading={generateLoading}
            onClick={canGenerate ? handleGenerate : undefined}
        >
            <span className={styles.GenerateBtnContent}>
                <span>{isUploading ? "上传中" : "生成"}</span>
                {isUploading ? (
                    <LoadingOutlined />
                ) : canGenerate && !generateLoading && (
                    <span className={styles.GenerateBtnCost}>
                        <img src={require("@assets/images/arcbean3.png")} alt="" />
                        <span>{totalCost}</span>
                    </span>
                )}
                {!isUploading && <span className={styles.TeamIcon}></span>}
            </span>
        </Button>
    );

    const uploadAfterWrap = (
        <div className={styles.SuccessUploadWrap}>
            <div className={styles.ImgList}>
                {matchImages.map((item, index) => (
                    <div className={styles.ImgItem} key={`${item.key || item.url}-${index}`}>
                        <img src={Utils.getImgThumbnail(item.url)} alt="" />
                        <span className={styles.ImgName}>图{index + 1}</span>
                        <div className={styles.DeleteIcon} onClick={() => handleDelete(index)}>
                            <Iconfont className={styles.Icon} type="icon-DeleteOutlined" />
                        </div>
                    </div>
                ))}
                {pendingUploadList.map((item, index) => (
                    <div
                        className={[
                            styles.ImgItem,
                            styles.PendingImgItem,
                            item.status === "uploading" ? styles.PendingImgItemUploading : "",
                        ].filter(Boolean).join(" ")}
                        key={item.id}
                    >
                        {item.previewUrl ? <img src={item.previewUrl} alt="" /> : <div className={styles.PendingImgFallback}></div>}
                        <span className={styles.ImgName}>图{matchImages.length + index + 1}</span>
                        <div className={styles.PendingMask}>
                            <LoadingOutlined className={styles.PendingIcon} />
                            <span className={styles.PendingText}>{item.status === "uploading" ? "上传中" : "等待上传"}</span>
                        </div>
                    </div>
                ))}
                {matchImages.length + pendingUploadList.length < MAX_UPLOAD_COUNT && (
                    <div className={styles.UploadItem}>
                        <input
                            ref={localUploadInputRef}
                            type="file"
                            accept="image/jpeg,image/png,image/webp,image/avif"
                            multiple
                            className={styles.HiddenFileInput}
                            onChange={handleLocalFileChange}
                        />
                        <Popover
                            trigger="hover"
                            placement="bottomLeft"
                            overlayClassName={styles.UploadPopover}
                            content={uploadMenuContent}
                            open={uploadMenuOpen}
                            onOpenChange={handleUploadMenuOpenChange}
                        >
                            <div className={styles.UploadTrigger} onClick={handleLocalUploadClick}>
                                <Iconfont className={styles.Icon} type="icon-general_dd" />
                            </div>
                        </Popover>
                    </div>
                )}
            </div>
        </div>
    );

    return (
        <div
            className={[
                styles.UploadInput,
                projectId ? styles.UploadInputFloating : "",
                dragging ? styles.UploadInputDragging : "",
            ].filter(Boolean).join(" ")}
            onDragEnterCapture={handleDragEnter}
            onDragOverCapture={handleDragOver}
            onDragLeaveCapture={handleDragLeave}
            onDropCapture={handleDrop}
        >
            {uploadAfterWrap}
            <div className={styles.InputContainer}>
                {showPlaceholder && (
                    <div
                        className={styles.PlaceholderOverlay}
                        data-placeholder="请描述你想要生成的图片效果，例如：生成一张高级时尚大片"
                    >
                    </div>
                )}
                <div
                    ref={contentEditableRef}
                    className={styles.ContenteditableWrap}
                    contentEditable
                    onInput={handleContentInput}
                    onFocus={() => setShowPlaceholder(false)}
                    onCompositionStart={handleCompositionStart}
                    onCompositionEnd={handleCompositionEnd}
                    suppressContentEditableWarning
                />
                <textarea
                    className={styles.TextArea}
                    value={prompt}
                    onChange={handlePromptChange}
                    placeholder="请描述你想要生成的图片效果，例如：生成一张高级时尚大片"
                />
            </div>
            <div className={styles.UploadParams}>
                <div className={styles.LeftGroups}>
                    <span className={styles.FixedModelTag}>
                        全能模型
                    </span>
                    <Select
                        value={imageSize}
                        className={styles.SmallSelect}
                        onChange={setImageSize}
                        options={SIZE_OPTIONS}
                    />
                    <Select
                        value={generateNum}
                        className={styles.SmallSelect}
                        onChange={setGenerateNum}
                        options={COUNT_OPTIONS}
                    />
                </div>
                {canGenerate ? (
                    generateButton
                ) : (
                    <Popover content={isUploading ? "图片上传中，请稍后再生成" : "请先上传图片或输入提示词"} trigger="hover">
                        <span className={styles.GenerateBtnPopoverTrigger}>{generateButton}</span>
                    </Popover>
                )}
            </div>
            <Drawer
                closable={false}
                onClose={handleDrawerClose}
                open={drawOpen}
                width={1900}
                destroyOnHidden={true}
            >
                <SelectTryonPose
                    ref={materialRef}
                    setDrawOpen={setDrawOpen}
                    drawOpen={drawOpen}
                    maxCount={MAX_UPLOAD_COUNT}
                    projectType={BATCH_IMAGES_TYPE.FUSIONWEAR_BATCH}
                    hideUploadTrigger
                    hideItemActions
                    selectedListScrollable
                    value={matchImages}
                    onChange={handleReferenceChange}
                    onClose={handleDrawerClose}
                    allowedTabs={REFERENCE_DRAWER_TABS}
                />
            </Drawer>
        </div>
    );
};

export default UploadInput;
